import { ChevronLeftIcon } from '@heroicons/react/16/solid'
import { useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Button } from '../catalyst/button'
import { Description, ErrorMessage, Field as FormField, FieldGroup, Label } from '../catalyst/fieldset'
import { Heading } from '../catalyst/heading'
import { Input } from '../catalyst/input'
import { Link } from '../catalyst/link'
import { RadioCards } from '../catalyst/radio-cards'
import { Select } from '../catalyst/select'
import { Text } from '../catalyst/text'
import { useAuth } from '../lib/auth'
import { formErrors, noErrors, type FormErrors } from '../lib/form-errors'
import { useFarms } from '../lib/farms'
import { createStockItem, inventoryPath, STOCK_CATEGORY_LABEL, STOCK_PACKAGE_NAMES, stockPath, useStockItem, type StockItemInput } from '../lib/inventory'
import { NameInput } from '../lib/name-input'
import { ProductPicker, type PickedProduct } from '../lib/product-picker'
import { emptyAmount, parseAmount, StockAmountInput, type AmountDraft } from '../lib/stock-amount-input'
import { orgStockUnit, STOCK_DIMENSION_LABEL, unitLong, unitsOf, unitShort } from '../lib/stock-units'
import type { StockCategory, StockDimension, StockItem } from '../lib/types'
import { Shell } from './shell'

// Adding or editing a stock item, as a page of its own. The category and what it is measured in
// (its dimension) are fixed once added; the unit it is shown in and how it comes (its package) can
// change any time, since amounts are stored in the dimension's canonical unit. A pesticide can link
// its registered product by name.

const DIMENSIONS = Object.keys(STOCK_DIMENSION_LABEL) as StockDimension[]
/** The display-unit select's value for "follow the org's setting". */
const ORG_SETTING = ''

function StockItemForm({
  initial,
  cancelHref,
  onSave,
  onDone,
}: {
  initial?: StockItem
  cancelHref: string
  onSave: (input: StockItemInput) => Promise<StockItem>
  onDone: (saved?: StockItem) => void
}) {
  const [category, setCategory] = useState<StockCategory>(initial?.category ?? 'PESTICIDE')
  const [name, setName] = useState(initial?.name ?? '')
  const { org } = useAuth()
  const [dimension, setDimension] = useState<StockDimension>(initial?.dimension ?? 'VOLUME')
  const [displayUnit, setDisplayUnit] = useState(initial?.displayUnit ?? ORG_SETTING)
  const [packageName, setPackageName] = useState(initial?.package?.name ?? '')
  const [packageSize, setPackageSize] = useState(initial?.package ? String(initial.package.size) : '')
  const [packageUnit, setPackageUnit] = useState(initial?.package?.unit ?? '')
  const [picked, setPicked] = useState<Partial<PickedProduct> | null>(
    initial?.product ? { productId: initial.pesticideProductId ?? undefined, pcpNumber: initial.product.pcpNumber, activeIngredients: initial.product.activeIngredients ?? '' } : null,
  )
  const [onHand, setOnHand] = useState<AmountDraft>(emptyAmount(''))
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<FormErrors>(noErrors)

  const orgUnit = orgStockUnit(dimension, org?.unitSystem)
  const shownIn = displayUnit || orgUnit
  const count = dimension === 'COUNT'
  // Unit selects start on the unit amounts are shown in, and follow it until changed.
  const pkgUnit = packageUnit && unitsOf(dimension).includes(packageUnit) ? packageUnit : shownIn
  const size = Number(packageSize)
  const pkg = packageName.trim() && packageSize.trim() && size > 0 ? { name: packageName.trim(), size, unit: pkgUnit } : null
  // A loose amount next to packages is most often in the package's unit ("3 jugs + 4.5 L").
  const onHandUnit = onHand.unit && unitsOf(dimension).includes(onHand.unit) ? onHand.unit : (pkg?.unit ?? shownIn)

  const invalid = (key: string) => key in errors.fields
  const message = (key: string) => (invalid(key) ? <ErrorMessage>{errors.fields[key]}</ErrorMessage> : null)

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!name.trim()) return
    setSubmitting(true)
    setErrors(noErrors)
    try {
      const amount = !initial ? parseAmount({ ...onHand, unit: onHandUnit }, !!pkg) : null
      const saved = await onSave({
        category,
        name: name.trim(),
        dimension,
        displayUnit: displayUnit || null,
        package: pkg,
        pesticideProductId: category === 'PESTICIDE' ? (picked?.productId ?? null) : null,
        onHand: amount ?? undefined,
      })
      onDone(saved)
    } catch (err) {
      setErrors(formErrors(err, 'Could not save the stock item.'))
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8">
      {errors.general && <Text className="mb-6 text-red-600 dark:text-red-400">{errors.general}</Text>}
      <FieldGroup>
        {initial ? (
          <Text>
            {STOCK_CATEGORY_LABEL[initial.category]} · {STOCK_DIMENSION_LABEL[initial.dimension].label.toLowerCase()}. This can't change once added.
          </Text>
        ) : (
          <RadioCards<StockCategory>
            label="What is it?"
            layout="wrap"
            value={category}
            options={(Object.keys(STOCK_CATEGORY_LABEL) as StockCategory[]).map((c) => ({ value: c, label: STOCK_CATEGORY_LABEL[c] }))}
            onChange={(c) => {
              setCategory(c)
              if (c !== 'PESTICIDE') setPicked(null)
            }}
          />
        )}

        <FormField>
          <Label>Name</Label>
          {category === 'PESTICIDE' ? (
            <ProductPicker
              name={name}
              picked={picked}
              onName={(text) => {
                setName(text)
                setPicked(null)
              }}
              onPick={({ productName, ...product }) => {
                setName(productName)
                setPicked(product)
              }}
              placeholder="Search registered products, or type a name"
              invalid={invalid('name') || invalid('pesticideProductId')}
              required
              autoFocus={!!initial}
            />
          ) : (
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              invalid={invalid('name')}
              placeholder={category === 'FERTILIZER' ? 'e.g. Ammonium sulphate 21-0-0' : 'e.g. SWD monitoring traps'}
              required
              autoFocus={!!initial}
            />
          )}
          {category === 'PESTICIDE' && <Description>Pick a registered product to link its PCP number and label.</Description>}
          {message('name') ?? message('pesticideProductId')}
        </FormField>

        {!initial && (
          <RadioCards<StockDimension>
            label="How is it measured?"
            value={dimension}
            options={DIMENSIONS.map((d) => ({ value: d, label: STOCK_DIMENSION_LABEL[d].label, detail: STOCK_DIMENSION_LABEL[d].detail }))}
            onChange={(d) => {
              setDimension(d)
              setDisplayUnit(ORG_SETTING)
              setPackageUnit('')
              setOnHand(emptyAmount(''))
            }}
          />
        )}

        {!count && (
          <FormField>
            <Label>Show amounts in</Label>
            <Select value={displayUnit} onChange={(e) => setDisplayUnit(e.target.value)} invalid={invalid('displayUnit')}>
              <option value={ORG_SETTING}>Farm setting ({unitShort(dimension, orgUnit)})</option>
              {unitsOf(dimension).map((u) => (
                <option key={u} value={u}>
                  {unitLong(dimension, u)}
                </option>
              ))}
            </Select>
            <Description>Amounts can be entered in any unit; this is how they are shown.</Description>
            {message('displayUnit')}
          </FormField>
        )}

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
          <FormField>
            <Label>Comes in</Label>
            <NameInput suggestions={STOCK_PACKAGE_NAMES} value={packageName} onChange={(e) => setPackageName(e.target.value)} placeholder="e.g. Jug" invalid={invalid('package.name')} />
            <Description>Optional. Lets you record and count in {packageName.trim() ? `${packageName.trim().toLowerCase()}s` : 'packages'}.</Description>
            {message('package.name')}
          </FormField>
          <FormField>
            <Label>{packageName.trim() ? `One ${packageName.trim().toLowerCase()} holds` : 'Package size'}</Label>
            <div data-slot="control" className="flex gap-2">
              <Input
                aria-label="Package size"
                type="number"
                inputMode="decimal"
                min="0"
                step="any"
                value={packageSize}
                onChange={(e) => setPackageSize(e.target.value)}
                invalid={invalid('package.size')}
                required={!!packageName.trim()}
                disabled={!packageName.trim()}
              />
              {!count && (
                <div className="w-32 shrink-0">
                  <Select aria-label="Package size unit" value={pkgUnit} onChange={(e) => setPackageUnit(e.target.value)} disabled={!packageName.trim()}>
                    {unitsOf(dimension).map((u) => (
                      <option key={u} value={u}>
                        {unitShort(dimension, u)}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
            </div>
            {message('package.size') ?? message('package.unit')}
          </FormField>
        </div>

        {!initial && (
          <StockAmountInput
            label="On hand now"
            description="Optional. Recorded as the first count."
            dimension={dimension}
            pkg={pkg}
            draft={{ ...onHand, unit: onHandUnit }}
            // Keep following the default unit until one is picked.
            onChange={(d) => setOnHand({ ...d, unit: !onHand.unit && d.unit === onHandUnit ? '' : d.unit })}
            invalid={invalid('onHand')}
          />
        )}
      </FieldGroup>

      {/* Sticky on phones so Save stays in reach with the keyboard up; inline from sm: up. */}
      <div className="sticky bottom-0 -mx-6 mt-10 flex justify-end gap-3 border-t border-zinc-950/10 bg-white px-6 py-4 sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 dark:border-white/10 dark:bg-zinc-900 sm:dark:bg-transparent">
        <Button plain href={cancelHref} onClick={(e: React.MouseEvent) => (e.preventDefault(), onDone())}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : initial ? 'Save' : 'Add stock item'}
        </Button>
      </div>
    </form>
  )
}

export function StockItemFormPage() {
  const { farmId, stockItemId } = useParams<{ farmId: string; stockItemId?: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { org } = useAuth()
  const { getFarm, loading: farmsLoading } = useFarms()
  const { item, loading, error, updateItem } = useStockItem(org?.id, farmId, stockItemId)
  const canWrite = org?.role !== 'AGRONOMIST'

  const farm = farmId ? getFarm(farmId) : undefined
  if (!farmsLoading && !farm) return <Navigate to="/" replace />
  if (!farm || !org || !farmId) return null

  const editing = !!stockItemId
  const back = editing ? stockPath(farmId, stockItemId) : inventoryPath(farmId, 'STOCK')
  if (!canWrite) return <Navigate to={back} replace />

  // As on the asset form: leave without leaving the form in history; adding lands on the new item.
  const done = (saved?: StockItem) => {
    if ((editing || !saved) && location.key !== 'default') navigate(-1)
    else navigate(saved ? stockPath(farmId, saved.id) : back, { replace: true })
  }

  return (
    <Shell>
      <Link href={back} className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
        <ChevronLeftIcon className="size-4" />
        {editing ? (item?.name ?? 'Back') : 'Stock'}
      </Link>

      <div className="mx-auto max-w-2xl">
        {!editing ? (
          <>
            <Heading className="mt-4">Add a stock item</Heading>
            <Text>One entry per product this farm keeps. Record deliveries, use and counts against it to keep on-hand current.</Text>
            <StockItemForm cancelHref={back} onSave={(input) => createStockItem(org.id, farmId, input)} onDone={done} />
          </>
        ) : loading ? (
          <Text className="mt-6">Loading…</Text>
        ) : !item || item.archivedAt ? (
          <div className="mt-6 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
            <Text>{error ?? (item?.archivedAt ? 'This has been archived and can no longer be edited.' : 'Not found.')}</Text>
          </div>
        ) : (
          <>
            <Heading className="mt-4">Edit {item.name}</Heading>
            <StockItemForm key={item.id} initial={item} cancelHref={back} onSave={updateItem} onDone={done} />
          </>
        )}
      </div>
    </Shell>
  )
}
